import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { zonesService } from '../../../services/zones';
import type { CragRouteSector } from '../../../types/crag-route';
import UploadFileButton from '../UploadFileButton';
import { confirmModelReplaced } from '../crag-model/confirm-model-replaced';
import { formatBytes, MAX_GLB_BYTES } from '../crag-model/format';

interface SectorModelCardProps {
  sector: CragRouteSector;
  crag: string;
  hasCragModel: boolean;
  /** The file is the crag's admins' to change. Everybody else is only told which it is. */
  canManage: boolean;
  onChanged: () => Promise<unknown>;
}

/**
 * Which 3D model this sector is read on: its own file, or its crag's. A new file has
 * coordinates of its own, so the lines drawn on the model in force stop showing. They
 * stay in the column, and the ones drawn on the crag's come back with the file gone.
 */
export default function SectorModelCard({ sector, crag, hasCragModel, canManage, onChanged }: SectorModelCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const own = sector.model_3d ?? null;

  const save = async (data: { model_3d_signed_id: string } | { remove_model_3d: true }, saved: string) => {
    setSaving(true);
    try {
      await zonesService.updateZone(crag, sector.id, data, 'crag');
      await onChanged();
      toast({ title: t(saved) });
    } catch (error) {
      toast({
        title: t('cragModel.current.saveError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    const count = sector.model_3d_lines_count ?? 0;
    if (count > 0 && !window.confirm(t('cragAdmin.routes.sectorModel.removeConfirm', { count }))) return;
    void save({ remove_model_3d: true }, 'cragModel.current.removed');
  };

  return (
    <section className="space-y-2 border-t pt-3" aria-label={t('cragAdmin.routes.sectorModel.title')}>
      <h3 className="text-sm font-medium">{t('cragAdmin.routes.sectorModel.title')}</h3>
      <p className="text-xs text-muted-foreground">
        {own ? (
          <>
            {own.filename} · {formatBytes(own.byte_size)}
          </>
        ) : (
          t(hasCragModel ? 'cragAdmin.routes.sectorModel.inherits' : 'cragAdmin.routes.sectorModel.none')
        )}
      </p>
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <UploadFileButton
            accept=".glb,model/gltf-binary"
            maxBytes={MAX_GLB_BYTES}
            disabled={saving}
            onUploaded={(signedId) => {
              if (confirmModelReplaced(sector, t)) void save({ model_3d_signed_id: signedId }, 'cragModel.current.uploaded');
            }}
            onError={(reason) =>
              toast({
                title: t(reason === 'too_large' ? 'cragModel.current.tooLarge' : 'cragModel.current.uploadError'),
                variant: 'destructive',
              })
            }
          >
            {t(own ? 'cragModel.current.replace' : 'cragModel.current.upload')}
          </UploadFileButton>
          {own && (
            <>
              <Button size="sm" variant="outline" className="gap-2" asChild>
                <a href={own.url} download={own.filename}>
                  <Download className="h-4 w-4" />
                  {t('cragModel.current.download')}
                </a>
              </Button>
              <Button size="sm" variant="ghost" className="gap-2 text-destructive" disabled={saving} onClick={remove}>
                <Trash2 className="h-4 w-4" />
                {t('cragModel.current.remove')}
              </Button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
